/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import type { EuiBasicTableColumn } from '@elastic/eui'
import { EuiCode, EuiFlexGroup, EuiFlexItem, EuiLink, EuiText, EuiTextColor } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import genericMessages from '@modules/project-lib/genericMessages'
import history from '@modules/utils/history'
import { projectUrl } from '@modules/project-user-lib/urls'
import type { AnyAdminProject } from '@modules/project-admin-api/types'
import { toLocaleDateTime } from '@modules/utils/format'
import type { Region } from '@modules/cluster-admin-api/v1/types'
import { isProjectSuspended } from '@modules/project-lib/suspended'
import FormattedRegionAndProvider from '@modules/project-components/FormattedRegionAndProvider'
import { getDisplayProjectId } from '@modules/project-lib/display'

export const projectNameColumn: EuiBasicTableColumn<AnyAdminProject> = {
  name: <FormattedMessage {...genericMessages.project} />,
  field: 'name',
  sortable: true,
  render: (name: AnyAdminProject['name'], project) => {
    const isSuspended = isProjectSuspended(project)

    return (
      <EuiText size='s'>
        <EuiFlexGroup gutterSize='m' justifyContent='flexStart' alignItems='baseline'>
          <EuiFlexItem grow={false}>
            <EuiCode css={{ whiteSpace: 'nowrap' }}>{getDisplayProjectId(project.id)}</EuiCode>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            {isSuspended ? (
              <EuiTextColor color='subdued'>
                {name} (<FormattedMessage {...genericMessages.suspended} />)
              </EuiTextColor>
            ) : (
              <strong>{name}</strong>
            )}
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiText>
    )
  },
}

export const regionColumn = (regions: Region[] | undefined) => ({
  name: (
    <FormattedMessage id='projects.list.header.region' defaultMessage='Cloud provider & region' />
  ),
  render: ({ region_id }: AnyAdminProject) => (
    <FormattedRegionAndProvider regionId={region_id} allRegions={regions} />
  ),
})

export const createdColumn: EuiBasicTableColumn<AnyAdminProject> = {
  name: (
    <span>
      <FormattedMessage {...genericMessages.createdAt} /> (UTC)
    </span>
  ),
  field: 'metadata.created_at',
  textOnly: true,
  sortable: true,
  render: (_ignore, { metadata }) => (
    <EuiText size='s' css={{ whiteSpace: 'nowrap' }}>
      {toLocaleDateTime(metadata.created_at)}
    </EuiText>
  ),
}

export const actionsColumn: EuiBasicTableColumn<AnyAdminProject> = {
  name: <FormattedMessage {...genericMessages.actions} />,
  align: 'right',
  render: ({ type, id }: AnyAdminProject) =>
    type === 'workplaceai' ? null : (
      <EuiFlexGroup
        gutterSize='l'
        justifyContent='flexEnd'
        alignItems='baseline'
        responsive={false}
      >
        <EuiFlexItem grow={false}>
          <EuiLink
            href={projectUrl(type, id)}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              history.push(projectUrl(type, id))
            }}
          >
            <FormattedMessage {...genericMessages.manage} />
          </EuiLink>
        </EuiFlexItem>
      </EuiFlexGroup>
    ),
}
